/**
 * useAppNavigate - Smart navigation hook for both modes
 *
 * In embedded mode (Bitrix24 iframe): opens in new tab
 * In regular mode: uses TanStack Router navigation
 */
import { useCallback } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';

type EmbeddedSearch = Record<string, string | number | boolean | undefined>;

interface NavigateOptions {
  /** Force open in new tab even in regular mode */
  forceNewTab?: boolean;
  /**
   * Internal destination when the current shell is Bitrix24 embedded.
   *
   * Path and search are kept apart on purpose: the embedded shell carries its
   * Bitrix24 session in the URL (DOMAIN, PROTOCOL, LANG, APP_SID), and those
   * params are merged forward here. Passing a ready-made `path?query` string to
   * the router would replace the whole search object and drop the session.
   */
  embeddedTo?: { to: string; search?: EmbeddedSearch };
  /**
   * History state for the destination - for what must not travel in the URL
   * (a client quote, #846). Lost when the destination opens in a new tab.
   */
  state?: Record<string, unknown>;
}

export function useAppNavigate() {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in embedded mode
  const isEmbedded = location.pathname.startsWith('/embedded');

  const appNavigate = useCallback(
    (to: string, options?: NavigateOptions) => {
      const { forceNewTab = false, embeddedTo, state } = options || {};

      if (isEmbedded && embeddedTo && !forceNewTab) {
        void navigate({
          to: embeddedTo.to,
          search: (previous: EmbeddedSearch) => ({
            ...previous,
            ...embeddedTo.search,
          }),
          ...(state ? { state } : {}),
        } as never);
        return;
      }

      // Embedded destinations without an internal equivalent open standalone.
      if (isEmbedded || forceNewTab) {
        const fullUrl = `${window.location.origin}${to}`;
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      // In regular mode: use TanStack Router navigation
      void navigate({ to, ...(state ? { state } : {}) } as never);
    },
    [navigate, isEmbedded]
  );

  return { appNavigate, isEmbedded };
}
